import React, { useEffect, useState } from "react";
import {
  updateFormContentPreview,
  getFormContentPreview,
  imageUploadClientPreview,
} from "../../../../api/services/services";
import { Grid, Button, Snackbar, Box } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { Bucket } from "../../../../api/config/apiConfig";
import ContactDetailField from "../../../molecules/ContactDetailField";
import TextArea from "../../../atoms/Textarea";
import DynamicCheckbox from "../../../molecules/DynamicCheckbox";
import generatePDF, { Resolution, Margin } from "react-to-pdf";
import html2pdf from "html2pdf.js";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";

const DeliveryRequestDownload = ({ formContent, surveyData }) => {
  const [formContentId, setFormContentId] = useState("");

  const [isEdit, setIsEdit] = useState(false);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const urlData = new URLSearchParams(location.search);
  const email = urlData.get("email");
  const surveyId = urlData.get("surveyId");
  const formId = urlData.get("formId");
  const token = urlData.get("token");

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th"; // 11th, 12th, 13th, etc.
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  // Helper function to format the date
  const formatDate = (dateString) => {
    const date = new Date(dateString);

    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();

    const ordinalSuffix = getOrdinalSuffix(day);

    return `${day}${ordinalSuffix} ${month}, ${year}`;
  };

  return (
    <>
      <div className="premium-print-container mx-4" style={{ pageBreakInside: 'avoid' }}>
        <div className="section-divider mb-3">
          <h1 className="section-title">Delivery Request Form</h1>
        </div>

        {/* customer detail*/}
        <div className="data-grid mb-4">
          <div className="data-item">
            <span className="data-label">Customer Name</span>
            <span className="data-value">{formContent?.clientName || "-"}</span>
          </div>
          <div className="data-item">
            <span className="data-label">Date</span>
            <span className="data-value">{formContent?.date ? formatDate(formContent?.date) : "-"}</span>
          </div>
        </div>

        {/* Delivery Address */}
        <div className="section-divider mb-2">
          <h2 className="section-title" style={{ fontSize: '11pt' }}>Delivery Details</h2>
        </div>
        <div className="mb-4">
          <span className="data-label">Delivery Address</span>
          <p className="p-word mt-1" style={{ fontSize: '10pt', color: '#1e293b' }}>
            {formContent?.deliveryAddress || "-"}
          </p>
        </div>

        {/* Date & Time */}
        <div className="data-grid mb-4" style={{ gridTemplateColumns: '1fr' }}>
          <div className="data-item">
            <span className="data-label">Preferred Packing Date & Time</span>
            <span className="data-value">
              {formContent?.deliveryDate ? formatDate(formContent?.deliveryDate) : "-"} 
              {formContent?.deliveryTime ? ` at ${formContent?.deliveryTime}` : ""}
            </span>
          </div>
        </div>

        {/* Access/Permissions */}
        <div className="section-divider mb-2">
          <h2 className="section-title" style={{ fontSize: '11pt' }}>Access & Permissions</h2>
        </div>
        <div className="mb-4">
          <p className="mb-2" style={{ fontSize: '10pt', fontWeight: 600 }}>
            Security access or permit required for team/vehicle?
          </p>
          <div className="d-flex gap-4">
            <span style={{ fontSize: '10pt', color: formContent?.permissions ? '#0f172a' : '#94a3b8' }}>
              [ {formContent?.permissions ? 'X' : ' '} ] Yes
            </span>
            <span style={{ fontSize: '10pt', color: !formContent?.permissions ? '#0f172a' : '#94a3b8' }}>
              [ {!formContent?.permissions ? 'X' : ' '} ] No
            </span>
          </div>
          {formContent?.permissions && formContent?.adviseRequirement && (
            <div className="mt-2 p-2" style={{ background: '#f8fafc', borderRadius: '4px', borderLeft: '4px solid #cbd5e1' }}>
              <span className="data-label">Requirements</span>
              <p className="mb-0" style={{ fontSize: '10pt' }}>{formContent.adviseRequirement}</p>
            </div>
          )}
        </div>

        {/* Priorities */}
        <div className="section-divider mb-2">
          <h2 className="section-title" style={{ fontSize: '11pt' }}>Receiving Priorities</h2>
        </div>
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={4}>
            <span className="data-label">1st Priority</span>
            <p style={{ fontSize: '10pt', margin: 0 }}>{formContent?.firstPriority || "-"}</p>
          </Grid>
          <Grid item xs={4}>
            <span className="data-label">2nd Priority</span>
            <p style={{ fontSize: '10pt', margin: 0 }}>{formContent?.secondPriority || "-"}</p>
          </Grid>
          <Grid item xs={4}>
            <span className="data-label">3rd Priority</span>
            <p style={{ fontSize: '10pt', margin: 0 }}>{formContent?.thirdPriority || "-"}</p>
          </Grid>
        </Grid>

        {/* Labels/Marking */}
        <div className="mb-4">
          <p className="mb-2" style={{ fontSize: '10pt', fontWeight: 600 }}>
            Rooms marked/labeled for item placement?
          </p>
          <div className="d-flex gap-4">
            <span style={{ fontSize: '10pt', color: formContent?.suitableInfo ? '#0f172a' : '#94a3b8' }}>
              [ {formContent?.suitableInfo ? 'X' : ' '} ] Yes
            </span>
            <span style={{ fontSize: '10pt', color: !formContent?.suitableInfo ? '#0f172a' : '#94a3b8' }}>
              [ {!formContent?.suitableInfo ? 'X' : ' '} ] No
            </span>
          </div>
        </div>

        {/* Remarks */}
        {(formContent?.suggestions || formContent?.officeInstructions) && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {formContent?.suggestions && (
              <Grid item xs={6}>
                <span className="data-label">Customer Suggestions</span>
                <p style={{ fontSize: '10pt', margin: 0 }}>{formContent.suggestions}</p>
              </Grid>
            )}
            {formContent?.officeInstructions && (
              <Grid item xs={6}>
                <span className="data-label">Office Instructions</span>
                <p style={{ fontSize: '10pt', margin: 0 }}>{formContent.officeInstructions}</p>
              </Grid>
            )}
          </Grid>
        )}

        {/* Signatures */}
        <div className="mt-5 pt-4">
          <Grid container spacing={4}>
            <Grid item xs={6} className="text-center">
              <div style={{ height: "80px", borderBottom: '1px solid #e2e8f0', marginBottom: '10px' }} className="d-flex justify-content-center align-items-center">
                {formContent.clientSign && (
                  <img src={`${Bucket}${formContent.clientSign}`} height={70} style={{ objectFit: 'contain' }} />
                )}
              </div>
              <p className="fw-bold mb-0" style={{ fontSize: '10pt' }}>Customer / Authorized Agent</p>
            </Grid>
            <Grid item xs={6} className="text-center">
              <div style={{ height: "80px", borderBottom: '1px solid #e2e8f0', marginBottom: '10px' }} className="d-flex justify-content-center align-items-center">
                {formContent?.adminSign && (
                  <img src={`${Bucket}${formContent.adminSign}`} height={70} style={{ objectFit: 'contain' }} />
                )}
              </div>
              <p className="fw-bold mb-0" style={{ fontSize: '10pt' }}>Shelozon Movers Packers & Logistics</p>
            </Grid>
          </Grid>
        </div>
      </div>
    </>
  );
};

export default DeliveryRequestDownload;
